// services/authFetch.js
// Helper para peticiones autenticadas sin redirecciones automáticas y con retry controlado

import { auth } from '../firebase';
import tokenCache from '../utils/tokenCache'; // 🚀 Token cache para optimización

// Para consumidores que necesitan comprobar sesión antes de hacer la petición.
export async function getAuthToken() {
  const user = auth.currentUser;
  return user ? tokenCache.getToken(user) : null;
}

/**
 * Obtiene un token de Firebase del usuario actual.
 * @param {boolean} refresh - Si true, fuerza la renovación del token.
 */
async function getToken(refresh = false) {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('AUTH_REQUIRED');
  }
  
  // 🚀 Usar token cache para mejor rendimiento
  if (refresh) {
    return tokenCache.forceRefresh(user);
  }
  return tokenCache.getToken(user);
}

/**
 * Un 401 renueva y reintenta una vez; un 403 solo se reintenta en GET si retry > 0.
 * No realiza redirecciones; devuelve el Response para que el llamador decida.
 * @param {RequestInfo} input
 * @param {RequestInit} init
 * @param {{ retry?: number }} options
 * @returns {Promise<Response>}
 */
export async function authFetch(input, init = {}, options = {}) {
  const retry = typeof options.retry === 'number' ? options.retry : 1;
  const method = (init.method || input?.method || 'GET').toUpperCase();
  const suppliedHeaders = Array.isArray(init.headers) ? Object.fromEntries(init.headers)
    : init.headers?.entries ? Object.fromEntries(init.headers.entries()) : { ...init.headers };
  // El token de una cabecera del consumidor no puede pisar la renovación.
  Object.keys(suppliedHeaders).forEach(key => {
    if (key.toLowerCase() === 'authorization') delete suppliedHeaders[key];
  });

  // Primer intento con token normal
  let token;
  try {
    token = await getToken(false);
  } catch (e) {
    // No hay usuario autenticado
    throw e;
  }

  const baseHeaders = {
    'Authorization': `Bearer ${token}`,
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0',
  };

  if (!(init.body instanceof FormData) && !suppliedHeaders['Content-Type']) {
    baseHeaders['Content-Type'] = 'application/json';
  }

  let response = await fetch(input, {
    ...init,
    headers: {
      ...baseHeaders,
      ...suppliedHeaders,
    },
  });

  // 401 ocurre antes del handler en las rutas protegidas; no equivale a reintentar un fallo de negocio.
  if (response.status === 401 || (response.status === 403 && method === 'GET' && retry > 0)) {
    const freshToken = await getToken(true);
    const retryHeaders = {
      'Authorization': `Bearer ${freshToken}`,
    };
    if (!(init.body instanceof FormData) && !suppliedHeaders['Content-Type']) {
      retryHeaders['Content-Type'] = 'application/json';
    }

    response = await fetch(input, {
      ...init,
      headers: {
        ...retryHeaders,
        ...suppliedHeaders,
      },
    });
  }

  return response;
}

/**
 * Variante que devuelve JSON y lanza error si !response.ok
 * @param {RequestInfo} input
 * @param {RequestInit} init
 * @param {{ retry?: number }} options
 */
export async function authFetchJson(input, init = {}, options = {}) {
  const res = await authFetch(input, init, options);
  // Manejar 204 No Content correctamente
  if (res.status === 204) {
    return null;
  }

  let data;
  try {
    data = await res.json();
  } catch (e) {
    // Si el servidor devolvió HTML/empty body y no era 204, crear un error legible
    throw new Error(res.ok ? 'INVALID_JSON' : `HTTP_${res.status}`);
  }
  if (!res.ok) {
    throw new Error(data?.error || data?.message || `HTTP_${res.status}`);
  }
  return data;
}
